import { getLastActiveDays } from '@/lib/activeWeek';

export type DayAnalytics = {
  date: string;
  label: string;
  dayLetter: string;
  intakeL: number;
  goalL: number;
  deficitL: number;
  volumeHit: boolean;
  frequencyHit: boolean;
};

type StatRow = {
  date: string;
  totalVolume?: number;
  targetVolume?: number;
  frequencyScore?: string | number;
  volumeScore?: string | number;
  successfulReminders?: number;
  totalReminders?: number;
};

function num(v: string | number | undefined): number {
  if (v === undefined || v === null) return 0;
  return typeof v === 'string' ? parseFloat(v) || 0 : v;
}

export function isVolumeGoalHit(stat?: StatRow, fallbackTarget = 2500): boolean {
  if (!stat) return false;
  const target = stat.targetVolume || fallbackTarget;
  return (stat.totalVolume || 0) >= target;
}

export function isFrequencyGoalHit(stat?: StatRow): boolean {
  if (!stat) return false;
  const score = num(stat.frequencyScore);
  if (score >= 80) return true;
  const total = stat.totalReminders || 0;
  const success = stat.successfulReminders || 0;
  if (total === 0) return false;
  return success / total >= 0.8;
}

export function buildWeekAnalytics(
  activeDays: string[],
  weeklyStats: StatRow[],
  fallbackTargetMl: number,
  anchor = new Date()
): {
  days: DayAnalytics[];
  volumeHits: number;
  frequencyHits: number;
  weeklyAverageL: number;
  bestDayLabel: string;
} {
  const window = getLastActiveDays(activeDays, 7, anchor);
  const map = new Map(weeklyStats.map((s) => [s.date, s]));

  const days: DayAnalytics[] = window.map((w) => {
    const stat = map.get(w.date);
    const goalMl = stat?.targetVolume || fallbackTargetMl;
    const intakeMl = stat?.totalVolume || 0;
    const intakeL = Number((intakeMl / 1000).toFixed(2));
    const goalL = Number((goalMl / 1000).toFixed(2));
    const deficitL = Number(Math.max(0, goalL - intakeL).toFixed(2));
    return {
      date: w.date,
      label: w.label,
      dayLetter: w.dayLetter,
      intakeL,
      goalL,
      deficitL,
      volumeHit: isVolumeGoalHit(stat, fallbackTargetMl),
      frequencyHit: isFrequencyGoalHit(stat),
    };
  });

  const volumeHits = days.filter((d) => d.volumeHit).length;
  const frequencyHits = days.filter((d) => d.frequencyHit).length;
  const totalL = days.reduce((s, d) => s + d.intakeL, 0);
  const weeklyAverageL = days.length > 0 ? Number((totalL / days.length).toFixed(1)) : 0;

  let bestDayLabel = '-';
  let maxL = -1;
  days.forEach((d) => {
    if (d.intakeL > maxL) {
      maxL = d.intakeL;
      bestDayLabel = d.label;
    }
  });

  return { days, volumeHits, frequencyHits, weeklyAverageL, bestDayLabel };
}

export function computeOrgRanks(
  leaderboard: Array<{
    userId: string;
    avgScore?: number;
    avgVolumeScore?: number;
    avgFrequencyScore?: number;
  }>,
  userId: string
): {
  volumeRank: number | null;
  frequencyRank: number | null;
  totalMembers: number;
  userVolumeScore: number;
  userFrequencyScore: number;
  orgVolumeScore: number;
  orgFrequencyScore: number;
} {
  const members = leaderboard.length;
  if (members === 0) {
    return {
      volumeRank: null,
      frequencyRank: null,
      totalMembers: 0,
      userVolumeScore: 0,
      userFrequencyScore: 0,
      orgVolumeScore: 0,
      orgFrequencyScore: 0,
    };
  }

  const byVolume = [...leaderboard].sort(
    (a, b) => (b.avgVolumeScore || 0) - (a.avgVolumeScore || 0)
  );
  const byFreq = [...leaderboard].sort(
    (a, b) => (b.avgFrequencyScore || 0) - (a.avgFrequencyScore || 0)
  );

  const volIdx = byVolume.findIndex((e) => e.userId === userId);
  const freqIdx = byFreq.findIndex((e) => e.userId === userId);
  const userEntry = leaderboard.find((e) => e.userId === userId);

  const orgVolumeScore = leaderboard.reduce((s, e) => s + (e.avgVolumeScore || 0), 0) / members;
  const orgFrequencyScore =
    leaderboard.reduce((s, e) => s + (e.avgFrequencyScore || 0), 0) / members;

  return {
    volumeRank: volIdx >= 0 ? volIdx + 1 : null,
    frequencyRank: freqIdx >= 0 ? freqIdx + 1 : null,
    totalMembers: members,
    userVolumeScore: userEntry?.avgVolumeScore || 0,
    userFrequencyScore: userEntry?.avgFrequencyScore || 0,
    orgVolumeScore,
    orgFrequencyScore,
  };
}
